define('presentation/signup/viewModels/signupProfileViewModel',[
    'businessServices/signup/signupEntryGate',
    'common/collections/enumerationKeyValueConverter',
    'common/promises/promiseFactory',
    'common/storage/workSessionIdStorage',
    'constants/howDidYouHearAboutUsEnumerations',
    'constants/signupSessionFlowConstants',
    'presentation/signup/common/signupNavigationManager',
    'presentation/signup/facades/signupProfileFacade',
    'presentation/signup/validators/signupProfileValidator'
], function() {

    return function() {
        let self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _i18n = require('i18next');
        const _workSessionIdStorage = require('common/storage/workSessionIdStorage');
        const _howDidYouHearAboutUs = require('constants/howDidYouHearAboutUsEnumerations');
        const _keyValueConverter = require('common/collections/enumerationKeyValueConverter');
        const _signupSessionFlowConstants = require('constants/signupSessionFlowConstants');

        const EMAIL_ALREADY_IN_USE_I18N_KEY = "emailAddressUnique";
        const NO_VERIFICATION_CODE = 0;

        let _facade = null;
        let _validator = null;
        let _disposables = [];
        let _signupEntryGate = null;
        let _signupNavigationManager = null;

        let _savedUserEmail = null;
        let _savingUserEmail = null;
        let _isEmailStepCompleted = false;

        const _isSignupForCustomer = _workSessionIdStorage.getWorkSessionId() !== null && _workSessionIdStorage.getWorkSessionId() !== undefined;

        self.headingText = _i18n.t('signupProfile:headingText');
        self.subtitleText = _i18n.t('signupProfile:subtitleText');

        self.userEmail = ko.observable('').extend({ rateLimit: 500 });
        self.showEmailField = ko.observable(false);
        self.firstName = ko.observable('').extend({ rateLimit: 500 });
        self.lastName = ko.observable('').extend({ rateLimit: 500 });
        self.mobileNumber = ko.observable('').extend({ rateLimit: 500 });
        self.countryAbbreviation = ko.observable('us');
        self.isSignupForCustomer = ko.observable(_isSignupForCustomer);
        self.isCompositionComplete = ko.observable(false);
        self.isNavigateToNextPageActive = ko.observable(true);
        self.isScrollActive = ko.observable(false);
        self.isValid = ko.observable(false);
        self.showValidation = ko.observable(false);
        self.signupPhoneNumbers = null;
        self.signupNumberType = null;

        self.howDidYouHearAboutUs = ko.observable('other');
        self.howDidYouHearAboutUsOther = ko.observable('').extend({ rateLimit: 500 });
        self.howDidYouHearAboutUsOptions = [];

        self.showHowDidYouHearAboutUsSelect = ko.observable(false);

        self.showHowDidYouHearAboutUsOther = ko.pureComputed(() => {
            return self.howDidYouHearAboutUs() && _howDidYouHearAboutUs[self.howDidYouHearAboutUs()] === _howDidYouHearAboutUs.other;
        });

        self.showHowDidYouHearAboutUs = ko.pureComputed(() => {
            return self.showHowDidYouHearAboutUsSelect() || self.showHowDidYouHearAboutUsOther();
        });

        // Surfaces the "already in use" error as soon as the async uniqueness check answers, rather
        // than holding it until submit the way every other field on this step behaves.
        self.showEmailValidation = ko.pureComputed(() => {
            if (self.showValidation()) {
                return true;
            }

            if (!ko.isObservable(self.userEmail.isValid)) {
                return false;
            }

            return self.userEmail.isValid() === false &&
                self.userEmail.validationMessage() === _i18n.t(EMAIL_ALREADY_IN_USE_I18N_KEY);
        });

        self.selectPlaceHolderMessage = ko.pureComputed(() => _i18n.t('signupProfile:select'));

        const _saveAndScreenEmailAddress = (userEmail) => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.saveEmailAddress(userEmail)
                    .fail(deferredObject.reject)
                    .done(() => {
                        if (userEmail !== _savedUserEmail) {
                            _isEmailStepCompleted = false;
                        }
                        _savedUserEmail = userEmail;

                        _signupEntryGate.evaluate(userEmail)
                            .done((verdict) => {
                                if (verdict.isAllowed) {
                                    deferredObject.resolve(true);
                                    return;
                                }

                                _signupNavigationManager.navigateToErrorPage();
                                deferredObject.resolve(false);
                            });
                    });
            });
        };

        const _setEmailStepCompleted = (userEmail) => {
            return _promiseFactory.defer((deferredObject) => {
                if (_isEmailStepCompleted) {
                    deferredObject.resolve();
                    return;
                }

                _facade.setEmailStepCompleted(userEmail, NO_VERIFICATION_CODE)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _isEmailStepCompleted = true;
                        deferredObject.resolve();
                    });
            });
        };

        const _completeStep = (userEmail) => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.checkIsFraudulentMobileNumber(self.mobileNumber(), self.countryAbbreviation())
                    .fail(deferredObject.reject)
                    .done((isFraudulent) => {
                        if (isFraudulent) {
                            _signupNavigationManager.navigateToErrorPage();
                            deferredObject.resolve(false);
                            return;
                        }

                        const setProfileCompleted = () => {
                            _facade.setProfileCompleted(true)
                                .fail(deferredObject.reject)
                                .done(() => deferredObject.resolve(true));
                        };

                        if (!self.showEmailField()) {
                            setProfileCompleted();
                            return;
                        }

                        _setEmailStepCompleted(userEmail)
                            .fail(deferredObject.reject)
                            .done(setProfileCompleted);
                    });
            });
        };

        self.onForwardArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        self.showValidation(true);

                        if (!isValid) {
                            deferredObject.resolve(false);
                            return;
                        }

                        if (!self.showEmailField()) {
                            _completeStep(null)
                                .fail(deferredObject.reject)
                                .done(deferredObject.resolve);
                            return;
                        }

                        const userEmail = self.userEmail();
                        _saveAndScreenEmailAddress(userEmail)
                            .fail(deferredObject.reject)
                            .done((isAllowed) => {
                                if (!isAllowed) {
                                    deferredObject.resolve(false);
                                    return;
                                }

                                _completeStep(userEmail)
                                    .fail(deferredObject.reject)
                                    .done(deferredObject.resolve);
                            });
                    });
            });
        };

        const _handleUserEmailChanged = (isValid) => {
            const userEmail = self.userEmail();
            if (isValid !== true || !userEmail || userEmail === _savedUserEmail || userEmail === _savingUserEmail) {
                return;
            }

            const clearSaving = () => {
                if (_savingUserEmail === userEmail) {
                    _savingUserEmail = null;
                }
            };

            _savingUserEmail = userEmail;
            _facade.saveEmailAddress(userEmail)
                .fail(clearSaving)
                .done(() => {
                    clearSaving();
                    if (userEmail !== _savedUserEmail) {
                        _isEmailStepCompleted = false;
                    }
                    _savedUserEmail = userEmail;

                    _signupEntryGate.saveAttribution(userEmail);
                });
        };

        const _handleFirstNameChanged = () => {
            if (self.firstName()) {
                _facade.saveFirstName(self.firstName());
            }
        };

        const _handleLastNameChanged = () => {
            if (self.lastName()) {
                _facade.saveLastName(self.lastName());
            }
        };

        const _handleMobileNumberChanged = () => {
            if (self.mobileNumber.isValid()) {
                let cleanPhoneNumber = self.mobileNumber().replace('+1', '');
                _facade.saveMobileNumber(cleanPhoneNumber);
            }
        };

        const _handleCountryAbbreviationChanged = () => {
            if (self.countryAbbreviation()) {
                _facade.saveMobileNumberCountryCode(self.countryAbbreviation());
            }
        };

        const _handleHowDidYouHearAboutUsOtherChanged = () => {
            if (self.howDidYouHearAboutUsOther()) {
                _facade.saveHowDidYouHearAboutUsOther(self.howDidYouHearAboutUsOther());
            }
        };

        const _populateHowDidYouHearAboutUsOptions = () => {
            self.howDidYouHearAboutUsOptions = _keyValueConverter.convertToKeyValues(_howDidYouHearAboutUs);
        };

        const _prePopulateInputValues = (signupProfilePresentationObject) => {
            const { emailAddress, isEmailStepCompleted, firstName, lastName, mobileNumber, mobileNumberCountryCode, howDidYouHearAboutUs, howDidYouHearAboutUsOther } = signupProfilePresentationObject;
            if (emailAddress) {
                self.userEmail(emailAddress);
                _savedUserEmail = emailAddress;
                _isEmailStepCompleted = !!isEmailStepCompleted;
            }
            if (firstName) {
                self.firstName(firstName);
            }
            if (lastName) {
                self.lastName(lastName);
            }
            if (mobileNumber) {
                self.mobileNumber(mobileNumber);
            }
            if (mobileNumberCountryCode) {
                self.countryAbbreviation(mobileNumberCountryCode);
            }
            if (howDidYouHearAboutUs && howDidYouHearAboutUs !== 'unknown') {
                self.howDidYouHearAboutUs(howDidYouHearAboutUs);
            }
            if (howDidYouHearAboutUsOther) {
                self.howDidYouHearAboutUsOther(howDidYouHearAboutUsOther);
            }

            if (firstName && lastName && mobileNumber) {
                self.isValid(true);
            }
        };

        self.detached = () => {
            _disposables.forEach(subscription => subscription.dispose());
            _disposables = [];
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/signup/facades/signupProfileFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/signup/validators/signupProfileValidator');
            _validator = new ValidatorConstructor();

            const SignupEntryGateConstructor = require('businessServices/signup/signupEntryGate');
            _signupEntryGate = new SignupEntryGateConstructor();
            _signupEntryGate.init();

            const SignupNavigationManagerConstructor = require('presentation/signup/common/signupNavigationManager');
            _signupNavigationManager = new SignupNavigationManagerConstructor();
            _signupNavigationManager.init();

            // Set before _initialize, because the validator registers its rules against it.
            const flowId = _signupNavigationManager.getFlowId();
            self.showEmailField(flowId === _signupSessionFlowConstants.flowA ||
                flowId === _signupSessionFlowConstants.flowAgent);

            return _initialize();
        };

        const _initialize = () => {
            _validator.registerViewModel(self, _facade);

            _populateHowDidYouHearAboutUsOptions();

            _promiseFactory.deferIndefinitely(deferredObject => {
                _facade.loadSignupSession()
                    .fail(deferredObject.reject)
                    .done((signupProfilePresentationObject) => {
                        _prePopulateInputValues(signupProfilePresentationObject);

                        self.signupPhoneNumbers = signupProfilePresentationObject.signupPhoneNumbers;
                        self.signupNumberType = signupProfilePresentationObject.signupNumberType;

                        _disposables.push(self.firstName.isValid.subscribe(_handleFirstNameChanged));
                        _disposables.push(self.lastName.isValid.subscribe(_handleLastNameChanged));
                        _disposables.push(self.countryAbbreviation.subscribe(_handleCountryAbbreviationChanged));
                        _disposables.push(self.mobileNumber.isValid.subscribe(_handleMobileNumberChanged));
                        _disposables.push(self.howDidYouHearAboutUsOther.subscribe(_handleHowDidYouHearAboutUsOtherChanged));

                        if (self.showEmailField()) {
                            _disposables.push(self.userEmail.isValid.subscribe(_handleUserEmailChanged));
                        }

                        deferredObject.resolve();
                    });
            });

            return _promiseFactory.wait();
        };
    };
});

